import { Money } from "@redotech/money";
import { COUNTRY_CURRENCIES } from "@redotech/money/countries";
import { Currency, isCurrency } from "@redotech/money/currencies";
import { CurrencyConverter } from "@redotech/money/exchange";
import { createEnum } from "@redotech/zod-util/zod-enum";
import type { FixedRate } from "./shipping-rate-table";

export const SHIPPING_OVERRIDE_CURRENCIES = [
  Currency.AUD,
  Currency.CAD,
  Currency.EUR,
  Currency.GBP,
  Currency.NOK,
  Currency.SEK,
  Currency.USD,
] as const;

export type ShippingOverrideCurrency =
  (typeof SHIPPING_OVERRIDE_CURRENCIES)[number];

export const shippingOverrideCurrencySchema = createEnum(
  SHIPPING_OVERRIDE_CURRENCIES,
  { enumName: "Currency", isPartial: true, originalEnum: Currency },
);

export function hasUniqueCurrencies(
  overrides: readonly { currency: string }[] | undefined,
): boolean {
  if (!overrides) {
    return true;
  }
  return (
    new Set(overrides.map((override) => override.currency)).size ===
    overrides.length
  );
}

export const UNIQUE_OVERRIDE_CURRENCIES_MESSAGE =
  "Each currency can only have one override.";

export function destinationLocalCurrency(
  destinationCountryCode: string | null | undefined,
): Currency | undefined {
  if (!destinationCountryCode) {
    return undefined;
  }
  const code = COUNTRY_CURRENCIES.getByCountry(
    destinationCountryCode.toUpperCase(),
  )?.currencyCode;
  return code && isCurrency(code) ? code : undefined;
}

export function resolveFixedRatePrice({
  rate,
  baseCurrency,
  requestCurrency,
  destinationCountryCode,
  exchangeRates,
}: {
  rate: Pick<FixedRate, "price" | "priceOverrides">;
  baseCurrency: Currency;
  requestCurrency: Currency;
  destinationCountryCode: string | null | undefined;
  exchangeRates: CurrencyConverter;
}): Money {
  const buyerCurrency = destinationLocalCurrency(destinationCountryCode);
  const override = buyerCurrency
    ? rate.priceOverrides?.find((o) => o.currency === buyerCurrency)
    : undefined;
  if (override) {
    return { amount: Number(override.price), currency: override.currency };
  }
  return exchangeRates.convert(
    { amount: Number(rate.price), currency: baseCurrency },
    requestCurrency,
  );
}
